import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  PhoneCall, Video, Users, ChevronDown, Trash2, CalendarClock, Clock,
  Sparkles, Mic, ListTodo, Loader2, Pencil, Check, X, Eye, MessageSquare, Send,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/constants";
import { RecordCallButton } from "@/components/RecordCallButton";
import { DSRAnalytics, DSRHistory } from "@/components/DSRAnalytics";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const ELEVATED = ["super_admin", "sales_head", "management"];
const TEAM_SCOPE = [...ELEVATED, "region_head", "sales_manager"];
const TYPE_META = {
  phone: { icon: PhoneCall, label: "Phone" },
  in_person: { icon: Users, label: "In-person" },
  video: { icon: Video, label: "Video" },
};

function time12(t) {
  if (!t) return "9:00 AM";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

const pad2 = (n) => String(n).padStart(2, "0");
const toLocalParts = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return { date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`, time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}` };
};
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

function EditCallWhen({ call, canEdit, onChange }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(() => toLocalParts(call.call_at));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const when = new Date(`${v.date}T${v.time || "12:00"}:00`);
    if (when.getTime() > Date.now() + 60000) { toast.error("Call date/time cannot be in the future."); return; }
    setBusy(true);
    try {
      await api.put(`/calls/${call.id}`, { call_at: when.toISOString() });
      toast.success("Call date updated");
      setEditing(false); onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`call-when-${call.id}`}>
      <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><Clock className="h-3 w-3 text-brand" /> Call date &amp; time</span>
      {editing ? (
        <>
          <input type="date" value={v.date} max={toLocalParts().date} onChange={(e) => setV({ ...v, date: e.target.value })}
            className="h-7 px-1.5 border border-slate-200 rounded bg-white text-xs" data-testid={`call-when-date-${call.id}`} />
          <input type="time" value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })}
            className="h-7 px-1.5 border border-slate-200 rounded bg-white text-xs" data-testid={`call-when-time-${call.id}`} />
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 h-7 px-2 rounded bg-brand text-white font-medium disabled:opacity-50" data-testid={`call-when-save-${call.id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
          </button>
          <button onClick={() => { setEditing(false); setV(toLocalParts(call.call_at)); }} className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-200 text-slate-500" data-testid={`call-when-cancel-${call.id}`}><X className="h-3 w-3" /></button>
        </>
      ) : (
        <>
          <span className="text-slate-700 font-medium" data-testid={`call-when-value-${call.id}`}>{fmtWhen(call.call_at)}</span>
          {canEdit && (
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-brand hover:underline font-medium" data-testid={`call-when-edit-${call.id}`}>
              <Pencil className="h-3 w-3" /> Edit
            </button>
          )}
        </>
      )}
    </div>
  );
}

function CallComments({ call, onChange }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const comments = call.comments || [];
  const isOwner = call.user_id === user?.id;
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/calls/${call.id}/comments`, { text });
      setText(""); toast.success(isOwner ? "Reply added" : "Coaching note sent to " + (call.user_name || "the rep")); onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/calls/${call.id}/comments/${id}`); onChange?.(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`call-comments-${call.id}`}>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1"><MessageSquare className="h-3 w-3 text-brand" /> {isOwner ? "Comments from your manager" : "Coaching notes"} {comments.length > 0 && <span className="text-slate-300">· {comments.length}</span>}</p>
      {comments.map((c) => (
        <div key={c.id} className={`rounded-md border px-3 py-2 text-sm ${c.by === user?.id ? "bg-brand/5 border-brand/20" : "bg-amber-50/60 border-amber-100"}`} data-testid={`call-comment-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-700">{c.by_name}{c.by_role === "region_head" || c.by_role === "sales_manager" || c.by_role === "sales_head" ? <span className="ml-1 text-[10px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">coach</span> : null}</span>
            <span className="text-[10px] text-slate-400">{new Date(c.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              {(c.by === user?.id || user?.role === "super_admin") && <button onClick={() => remove(c.id)} className="ml-2 text-slate-300 hover:text-red-500" title="Delete" data-testid={`call-comment-delete-${c.id}`}><X className="h-3 w-3 inline" /></button>}
            </span>
          </div>
          <p className="text-slate-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder={isOwner ? "Reply to your manager…" : `Leave a coaching note for ${call.user_name || "the rep"}…`}
          className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-brand" data-testid={`call-comment-input-${call.id}`} />
        <button onClick={send} disabled={busy || !text.trim()} className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-brand text-white text-sm font-medium disabled:opacity-50" data-testid={`call-comment-send-${call.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
        </button>
      </div>
    </div>
  );
}

function CallCard({ call, onChange, isAdmin, canEditWhen, viewOnly }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const Meta = TYPE_META[call.call_type] || TYPE_META.phone;
  const Icon = Meta.icon;
  const aps = call.action_points || [];
  const doneCount = aps.filter((a) => a.done).length;

  const toggle = async (apId) => {
    try {
      await api.post(`/calls/${call.id}/action-points/${apId}/toggle`);
      qc.invalidateQueries({ queryKey: ["calls"] });
      qc.invalidateQueries({ queryKey: ["follow-ups"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/calls/${call.id}`);
      toast.success("Call log deleted");
      onChange?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="overflow-hidden" data-testid={`call-card-${call.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors" data-testid={`call-toggle-${call.id}`}>
        <span className="h-9 w-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center flex-shrink-0">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-900 truncate">{call.company || "Untitled call"}</p>
            {call.source === "recording" && <Mic className="h-3 w-3 text-brand" title="Recorded" />}
            {aps.length > 0 && (
              <Badge variant="secondary" className="text-[10px] gap-1">
                <ListTodo className="h-3 w-3" /> {doneCount}/{aps.length}
              </Badge>
            )}
            {(call.comments || []).length > 0 && (
              <Badge variant="secondary" className="text-[10px] gap-1 bg-amber-50 text-amber-700" data-testid={`call-comments-badge-${call.id}`}>
                <MessageSquare className="h-3 w-3" /> {call.comments.length}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 line-clamp-1 mt-0.5">{call.summary || "No summary"}</p>
          <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            <span>{Meta.label}</span>
            {call.contact_name && <span>· {call.contact_name}</span>}
            <span>· {formatDate(call.call_at)}</span>
            {isAdmin && call.user_name && <span>· by {call.user_name}</span>}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-4" data-testid={`call-detail-${call.id}`}>
          <EditCallWhen call={call} canEdit={canEditWhen} onChange={onChange} />
          {call.minutes && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1"><Sparkles className="h-3 w-3 text-brand" /> Minutes</p>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{call.minutes}</pre>
            </div>
          )}

          {aps.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 flex items-center gap-1"><CalendarClock className="h-3 w-3 text-brand" /> Action points</p>
              <div className="space-y-1.5">
                {aps.map((ap) => (
                  <div key={ap.id} className="flex items-start gap-2.5 text-sm" data-testid={`call-detail-ap-${ap.id}`}>
                    <Checkbox checked={ap.done} disabled={viewOnly} onCheckedChange={() => !viewOnly && toggle(ap.id)} className="mt-0.5" data-testid={`call-detail-ap-toggle-${ap.id}`} />
                    <div className="min-w-0">
                      <p className={`${ap.done ? "line-through text-slate-400" : "text-slate-700"}`}>{ap.text}</p>
                      {ap.due_date && (
                        <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" /> {formatDate(ap.due_date)} · {time12(ap.due_time)}
                          {!ap.done && ap.follow_up_id && <span className="text-brand">· reminder set</span>}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <CallComments call={call} onChange={onChange} />
          {viewOnly && <p className="text-[11px] text-slate-400 flex items-center gap-1" data-testid={`call-view-only-${call.id}`}><Eye className="h-3 w-3" /> View only — logged by {call.user_name}</p>}
          {!viewOnly && <div className="flex justify-end pt-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-red-600 transition-colors" data-testid={`call-delete-${call.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this call log?</AlertDialogTitle>
                  <AlertDialogDescription>The minutes and any scheduled reminders will be removed. This can't be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid={`call-delete-confirm-${call.id}`}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>}
        </div>
      )}
    </Card>
  );
}

export default function DailyCalls() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = TEAM_SCOPE.includes(user?.role);
  const [scope, setScope] = useState(TEAM_SCOPE.includes(user?.role) ? "all" : "mine");
  const [view, setView] = useState("calls");
  const [person, setPerson] = useState("all");
  const [days, setDays] = useState("30");

  const { data: members } = useQuery({
    queryKey: ["team-members"], enabled: isAdmin,
    queryFn: async () => (await api.get("/team-activity/members")).data?.members || [],
  });
  const { data: calls = [], isLoading, refetch } = useQuery({
    queryKey: ["calls", scope, person, days],
    queryFn: async () => (await api.get("/calls", { params: {
      ...(scope === "all" ? { all: true, ...(person !== "all" ? { user_id: person } : {}) } : {}),
      days: Number(days) || 0, limit: 1000 } })).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["calls"] });

  return (
    <div className={`${view !== "calls" ? "max-w-6xl" : "max-w-3xl"} mx-auto space-y-6`} data-testid="daily-calls-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <PhoneCall className="h-6 w-6 text-brand" /> Daily Calls
          </h1>
          <p className="text-sm text-slate-500 mt-1">Record or log your sales calls — AI writes the minutes and sets reminders for every action point.</p>
        </div>
        <RecordCallButton onSaved={refresh} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="calls-view-toggle">
          <button onClick={() => setView("calls")} className={`text-sm px-4 py-1.5 rounded-md font-medium transition-colors ${view === "calls" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="calls-view-calls">Calls</button>
          <button onClick={() => setView("analytics")} className={`text-sm px-4 py-1.5 rounded-md font-medium transition-colors ${view === "analytics" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="calls-view-analytics">Analytics (DSR)</button>
          {isAdmin && <button onClick={() => setView("history")} className={`text-sm px-4 py-1.5 rounded-md font-medium transition-colors ${view === "history" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="calls-view-history">History</button>}
        </div>
        {view === "calls" && isAdmin && (
          <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="calls-scope-toggle">
            <button onClick={() => setScope("mine")} className={`text-sm px-4 py-1.5 rounded-md font-medium transition-colors ${scope === "mine" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="calls-scope-mine">My calls</button>
            <button onClick={() => setScope("all")} className={`text-sm px-4 py-1.5 rounded-md font-medium transition-colors ${scope === "all" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid="calls-scope-all">All team</button>
          </div>
        )}
        {view === "calls" && isAdmin && scope === "all" && (
          <Select value={person} onValueChange={setPerson}>
            <SelectTrigger className="h-9 w-[190px] bg-white" data-testid="calls-person-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone in team</SelectItem>
              {(members || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {view === "calls" && (
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="calls-range-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
              <SelectItem value="0">All history</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {view === "history" ? (
        <DSRHistory />
      ) : view === "analytics" ? (
        <DSRAnalytics />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : calls.length === 0 ? (
        <Card className="py-16 text-center" data-testid="calls-empty">
          <PhoneCall className="h-10 w-10 text-slate-200 mx-auto" />
          <p className="text-sm font-medium text-slate-600 mt-3">No call logs yet</p>
          <p className="text-xs text-slate-400 mt-1">Tap "Log a Call" to record one — or type the minutes manually.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {calls.map((c) => <CallCard key={c.id} call={c} onChange={refresh} isAdmin={isAdmin && scope === "all"} canEditWhen={c.user_id === user?.id || user?.role === "super_admin"}
            viewOnly={c.user_id !== user?.id && user?.role !== "super_admin"} />)}
        </div>
      )}
    </div>
  );
}
